import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SelectionDraft, SelectionInfo, SelectionTool } from './types';
import { SELECTION_NAME_MAX } from './selectionForm';

const TOOLS: SelectionTool[] = [
  'add-one', 'add-zone', 'remove-one', 'remove-zone',
];
const NO_MEMBERS: string[] = [];
const INITIAL_SELECTIONS: SelectionInfo[] = [
  { id: 'demo-2', name: 'Demo 2', color: '#0046AF', networkId: '', consumption: 'Chaud' },
  { id: 'demo-3', name: 'Demo 3', color: '#4FB400', networkId: '', consumption: 'Chaud' },
];

/** Local fixtures only; source: buildingIds → validate/resetMode in production. Plan : membres injectés depuis le projet. */
export function useSelection(heatById: Record<string, number | null>, initialMembers: Record<string, string[]>) {
  const [selections, setSelections] = useState(INITIAL_SELECTIONS);
  const [selectionId, setSelectionId] = useState<string | null>('demo-2');
  const [membersBySelection, setMembersBySelection] = useState<Record<string, string[]>>({});
  const current = selections.find((s) => s.id === selectionId) ?? null;
  const members = (selectionId && (membersBySelection[selectionId] ?? initialMembers[selectionId])) || NO_MEMBERS;
  const selectionLabel = current?.name ?? '';
  const [activeTool, setActiveTool] = useState<SelectionTool | null>(null);
  const [pending, setPending] = useState<string[]>([]);
  const cancel = useCallback(() => { setActiveTool(null); setPending([]); }, []);
  const changeSelection = (id: string) => {
    if (!selections.some((s) => s.id === id)) throw new Error('Sélection inconnue.');
    if (activeTool) return;
    setSelectionId(id);
  };
  /** « Quitter la sélection » : retour à l'accueil sans sélection (goToHome en production). */
  const quit = () => { cancel(); setSelectionId(null); };
  const create = (draft: SelectionDraft) => {
    const name = draft.name.trim();
    if (!name || name.length > SELECTION_NAME_MAX) throw new Error('Nom de sélection invalide.');
    const id = `selection-${crypto.randomUUID()}`;
    setSelections((prev) => [...prev, { ...draft, name, id }]);
    setMembersBySelection((prev) => ({ ...prev, [id]: [] }));
    cancel();
    setSelectionId(id);
    return id;
  };
  useEffect(() => {
    const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [cancel]);
  const selectTool = (id: string) => {
    if (!selectionId || !TOOLS.includes(id as SelectionTool) || activeTool) return;
    setPending([]);
    setActiveTool(id as SelectionTool);
  };
  const pickBuilding = (id: string) => {
    if (activeTool !== 'add-one' && activeTool !== 'remove-one') return false;
    if (!(id in heatById)) return true;
    if (activeTool === 'add-one' ? members.includes(id) : !members.includes(id)) return true;
    setPending((prev) => prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]);
    return true;
  };
  const validate = () => {
    if (!selectionId || !pending.length || (activeTool !== 'add-one' && activeTool !== 'remove-one')) return;
    setMembersBySelection((prev) => ({
      ...prev,
      [selectionId]: activeTool === 'add-one'
        ? [...members, ...pending]
        : members.filter((id) => !pending.includes(id)),
    }));
    cancel();
  };
  const summary = useMemo(() => ({
    buildings: members.length,
    heatTotal: members.some(id => heatById[id] === null || heatById[id] === undefined)
      ? null : members.reduce((sum, id) => sum + heatById[id]!, 0),
    // Network geometry is owned by useNetwork; composed into the panel in index.tsx.
    pipes: 0,
  }), [heatById, members, selectionId]);
  const instruction = !activeTool ? null
    : activeTool === 'add-one' || activeTool === 'remove-one'
      ? `Simulation locale — cliquez sur les bâtiments ${activeTool === 'add-one' ? 'hors sélection' : 'de la sélection'} sur la carte. ${pending.length} bâtiment(s) en attente. Annuler ou Échap abandonne les changements.`
      : 'Limite du prototype — le lasso bâtiments n’est pas reproduit ici. Aucun changement simulé automatiquement. Annuler ou Échap quitte cet outil ; Valider reste désactivé.';
  return { selections, current, selectionId, selectionLabel, changeSelection, quit, create, members, activeTool, pending, cancel, selectTool, pickBuilding, validate, summary, instruction };
}
